// ═══════════════════════════════════════════════════════════════════════════
// GESTION RÉCOLTE - Interface de gestion du stock par période
// ═══════════════════════════════════════════════════════════════════════════
// frontend/src/modules/admin/components/GestionRecolte.jsx
// Version avec :
// - Sélection auto période en cours
// - Bascule période (report stock dimanche soir)
// - Stock Départ modifiable (les vers 🐛)
// - "Retrait dès le" au lieu de "Livraison"
// Farm-Booking - Papy 73+
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import { 
  AlertTriangle, 
  Check, 
  RefreshCw, 
  Plus, 
  Edit2, 
  Save, 
  X,
  Leaf,
  Package,
  TrendingUp,
  TrendingDown,
  Download,
  Upload,
  FileSpreadsheet,
  ArrowRightLeft,
  Calendar
} from 'lucide-react';

const GestionRecolte = () => {
  // ═══════════════════════════════════════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════════════════════════════════════
  const [periodes, setPeriodes] = useState([]);
  const [selectedPeriode, setSelectedPeriode] = useState(null);
  const [stocks, setStocks] = useState([]);
  const [alertes, setAlertes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [message, setMessage] = useState(null);
  const [importing, setImporting] = useState(false);
  const [infoBascule, setInfoBascule] = useState(null);
  const [basculing, setBasculing] = useState(false);
  
  const fileInputRef = useRef(null);

  // ═══════════════════════════════════════════════════════════════════════════
  // CHARGEMENT INITIAL
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    loadPeriodes();
    loadAlertes();
  }, []);

  useEffect(() => {
    if (selectedPeriode) {
      loadStocks(selectedPeriode);
      loadInfoBascule(selectedPeriode);
    }
  }, [selectedPeriode]);

  // ═══════════════════════════════════════════════════════════════════════════
  // API CALLS
  // ═══════════════════════════════════════════════════════════════════════════
  const loadPeriodes = async () => {
    try {
      const res = await fetch('/api/recolte/periodes-actives');
      const data = await res.json();
      if (data.success) {
        setPeriodes(data.data);
        if (data.data.length > 0 && !selectedPeriode) {
          // ════════════════════════════════════════════════════════════════
          // SÉLECTION AUTOMATIQUE : Période en cours ou prochaine
          // ════════════════════════════════════════════════════════════════
          const now = new Date();
          
          // Chercher la période EN COURS (date actuelle entre début et fin résa)
          let periodeEnCours = data.data.find(p => {
            const debut = new Date(p.date_debut_reservations);
            const fin = new Date(p.date_fin_reservations);
            return now >= debut && now <= fin;
          });
          
          // Si pas de période en cours, prendre la PROCHAINE (livraison future la plus proche)
          if (!periodeEnCours) {
            periodeEnCours = data.data.find(p => new Date(p.date_livraison) >= now);
          }
          
          // Sinon prendre la première par défaut
          setSelectedPeriode(periodeEnCours?.id || data.data[0].id);
        }
      }
    } catch (error) {
      console.error('Erreur chargement périodes:', error);
      showMessage('Erreur chargement périodes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadStocks = async (periodeId) => {
    try {
      setLoading(true);
      const res = await fetch(`/api/recolte/stock-periode/${periodeId}`);
      const data = await res.json();
      if (data.success) {
        setStocks(data.data);
      }
    } catch (error) {
      console.error('Erreur chargement stocks:', error);
      showMessage('Erreur chargement stocks', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadAlertes = async () => {
    try {
      const res = await fetch('/api/recolte/alertes');
      const data = await res.json();
      if (data.success) {
        setAlertes(data.data);
      }
    } catch (error) {
      console.error('Erreur chargement alertes:', error);
    }
  };

  const loadInfoBascule = async (periodeId) => {
    try {
      const res = await fetch(`/api/recolte/info-bascule/${periodeId}`);
      const data = await res.json();
      if (data.success) {
        setInfoBascule(data.data);
      }
    } catch (error) {
      console.error('Erreur info bascule:', error);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // ACTIONS
  // ═══════════════════════════════════════════════════════════════════════════
  const handleInitPeriode = async () => {
    if (!selectedPeriode) return;
    
    try {
      const res = await fetch(`/api/recolte/init-periode/${selectedPeriode}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reprendre_stock: false })
      });
      const data = await res.json();
      
      if (data.success) {
        showMessage(`✅ ${data.message}`, 'success');
        loadStocks(selectedPeriode);
      } else {
        showMessage(`❌ ${data.message}`, 'error');
      }
    } catch (error) {
      showMessage('Erreur initialisation', 'error');
    }
  };

  const handleSyncReservations = async () => {
    if (!selectedPeriode) return;
    
    try {
      const res = await fetch(`/api/recolte/sync-reservations/${selectedPeriode}`, {
        method: 'POST'
      });
      const data = await res.json();
      
      if (data.success) {
        showMessage(`✅ ${data.message}`, 'success');
        loadStocks(selectedPeriode);
      } else {
        showMessage(`❌ ${data.message}`, 'error');
      }
    } catch (error) {
      showMessage('Erreur synchronisation', 'error');
    }
  };

  // ════════════════════════════════════════════════════════════════════════════
  // BASCULE PÉRIODE - Report stock du dimanche soir 🔄
  // ════════════════════════════════════════════════════════════════════════════
  const handleBasculePeriode = async () => {
    if (!selectedPeriode || !infoBascule?.hasPrecedente) return;
    
    if (!window.confirm(
      `🔄 Reporter le stock restant de ${infoBascule.precedente.nom} ?\n\n` +
      `${infoBascule.restes.nb_produits} produit(s) - Total: ${infoBascule.restes.total} unités\n\n` +
      `Le stock sera ajouté au "Stock Départ" de la période actuelle.`
    )) return;

    setBasculing(true);
    try {
      const res = await fetch(`/api/recolte/bascule-periode/${selectedPeriode}`, {
        method: 'POST'
      });
      const data = await res.json();
      
      if (data.success) {
        showMessage(`🔄 ${data.message}`, 'success');
        loadStocks(selectedPeriode);
        loadInfoBascule(selectedPeriode);
      } else {
        showMessage(`❌ ${data.message}`, 'error');
      }
    } catch (error) {
      showMessage('Erreur bascule période', 'error');
    } finally {
      setBasculing(false);
    }
  };

  const handleEdit = (stock) => {
    setEditingId(stock.id);
    setEditValues({
      qte_previsionnelle: stock.qte_previsionnelle || 0,
      qte_talon_boutique: stock.qte_talon_boutique || 0,
      qte_recoltee: stock.qte_recoltee || ''
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValues({});
  };

  const handleSaveEdit = async (stockId) => {
    try {
      const res = await fetch(`/api/recolte/stock/${stockId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editValues)
      });
      const data = await res.json();
      
      if (data.success) {
        showMessage('✅ Sauvegardé', 'success');
        setEditingId(null);
        loadStocks(selectedPeriode);
        loadAlertes();
      } else {
        showMessage(`❌ ${data.message}`, 'error');
      }
    } catch (error) {
      showMessage('Erreur sauvegarde', 'error');
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // EXPORT / IMPORT EXCEL
  // ═══════════════════════════════════════════════════════════════════════════
  const handleExport = () => {
    if (!selectedPeriode) return;
    window.location.href = `/api/recolte/export/${selectedPeriode}`;
    showMessage('📥 Téléchargement Excel en cours...', 'success');
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !selectedPeriode) return;

    setImporting(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`/api/recolte/import/${selectedPeriode}`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();

      if (data.success) {
        let msg = `✅ ${data.data.updated} produits mis à jour`;
        if (data.data.alertes > 0) {
          msg += ` ⚠️ ${data.data.alertes} alertes stock`;
        }
        showMessage(msg, data.data.alertes > 0 ? 'warning' : 'success');
        loadStocks(selectedPeriode);
        loadAlertes();
      } else {
        showMessage(`❌ ${data.message}`, 'error');
      }
    } catch (error) {
      showMessage('Erreur import fichier', 'error');
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════════════════════════════════════
  const showMessage = (text, type = 'info') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  const getStatutBadge = (statut) => {
    const styles = {
      'alerte_stock': 'bg-red-100 text-red-700 animate-pulse border-red-300',
      'recolte_ok': 'bg-green-100 text-green-700 border-green-300',
      'en_recolte': 'bg-yellow-100 text-yellow-700 border-yellow-300',
      'previsionnel': 'bg-gray-100 text-gray-700 border-gray-300',
      'ouvert_resa': 'bg-blue-100 text-blue-700 border-blue-300',
      'distribue': 'bg-purple-100 text-purple-700 border-purple-300',
      'cloture': 'bg-gray-200 text-gray-500 border-gray-400'
    };
    
    const labels = {
      'alerte_stock': '⚠️ ALERTE',
      'recolte_ok': '✅ OK',
      'en_recolte': '🌾 Récolte',
      'previsionnel': '📝 Prévu',
      'ouvert_resa': '🛒 Ouvert',
      'distribue': '📦 Distribué',
      'cloture': '🔒 Clôturé'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${styles[statut] || styles.previsionnel}`}>
        {labels[statut] || statut}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit' 
    });
  };

  const formatDateLong = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', { 
      weekday: 'long',
      day: '2-digit', 
      month: 'long'
    });
  };

  const periodeSelectionnee = periodes.find(p => p.id === selectedPeriode);

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className="space-y-4">
      {/* MESSAGE TOAST */}
      {message && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
          message.type === 'error' ? 'bg-red-500 text-white' :
          message.type === 'warning' ? 'bg-orange-500 text-white' :
          message.type === 'success' ? 'bg-green-500 text-white' :
          'bg-blue-500 text-white'
        }`}>
          {message.text}
        </div>
      )}

      {/* INPUT FICHIER CACHÉ */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleImportFile}
        accept=".xlsx,.xls"
        className="hidden"
      />

      {/* EN-TÊTE */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Leaf className="h-7 w-7 text-green-600" />
            🌱 Gestion Récolte
          </h2>
          <p className="text-gray-500 mt-1">Stock prévisionnel, récolte et suivi par période</p>
        </div>

        {/* SÉLECTEUR PÉRIODE */}
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={selectedPeriode || ''}
            onChange={(e) => setSelectedPeriode(Number(e.target.value))}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 bg-white font-medium"
          >
            {periodes.map(p => (
              <option key={p.id} value={p.id}>
                {p.nom} {p.nb_alertes > 0 ? `⚠️ (${p.nb_alertes})` : ''}
              </option>
            ))}
          </select>

          <button
            onClick={() => loadStocks(selectedPeriode)}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            title="Rafraîchir"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* ALERTES GLOBALES */}
      {alertes.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-center gap-2 text-red-700 font-medium">
            <AlertTriangle className="h-5 w-5" />
            {alertes.length} alerte(s) stock en cours
          </div>
        </div>
      )}

      {/* INFOS PÉRIODE + BOUTONS ACTION */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Infos période */}
          {periodeSelectionnee && (
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span className="text-gray-500">Réservations:</span>
                <span className="font-medium">
                  {formatDate(periodeSelectionnee.date_debut_reservations)} → {formatDate(periodeSelectionnee.date_fin_reservations)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Réservations:</span>
                <span className="font-bold text-blue-600">{periodeSelectionnee.nb_reservations || 0}</span>
              </div>
            </div>
          )}

          {/* Boutons actions */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Bouton Bascule Période */}
            {infoBascule?.hasPrecedente && !infoBascule.dejaBasule && infoBascule.restes.nb_produits > 0 && (
              <button
                onClick={handleBasculePeriode}
                disabled={basculing}
                className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm"
                title={`Reporter ${infoBascule.restes.nb_produits} produit(s) depuis ${infoBascule.precedente.nom}`}
              >
                <ArrowRightLeft className="h-4 w-4" />
                {basculing ? 'Bascule...' : `Bascule ${infoBascule.precedente.nom}`}
              </button>
            )}

            {infoBascule?.dejaBasule && (
              <span className="text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded">
                ✅ Stock reporté
              </span>
            )}

            <button
              onClick={handleSyncReservations}
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              <RefreshCw className="h-4 w-4" />
              Sync Résa
            </button>

            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
            >
              <Download className="h-4 w-4" />
              Export Excel
            </button>

            <button
              onClick={handleImportClick}
              disabled={importing}
              className="flex items-center gap-2 px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 text-sm"
            >
              <Upload className="h-4 w-4" />
              {importing ? 'Import...' : 'Import Excel'}
            </button>
          </div>
        </div>
      </div>

      {/* ENCART WORKFLOW EXCEL */}
      <div className="bg-gradient-to-r from-emerald-50 to-orange-50 border border-emerald-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <FileSpreadsheet className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-1" />
          <div>
            <h4 className="font-medium text-emerald-800">📊 Workflow Excel recommandé</h4>
            <ol className="mt-2 text-sm text-emerald-700 space-y-1 list-decimal list-inside">
              <li><strong>Export Excel</strong> → Télécharge le fichier avec tous les produits</li>
              <li><strong>Modifie dans Excel</strong> → Remplis les colonnes bleues (Prévu, Stock Départ, Récolté)</li>
              <li><strong>Import Excel</strong> → Réimporte le fichier modifié</li>
              <li>Les alertes stock sont détectées automatiquement !</li>
            </ol>
          </div>
        </div>
      </div>

      {/* TABLEAU STOCKS */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">
          Chargement...
        </div>
      ) : stocks.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">
            Aucun produit initialisé pour cette période
          </p>
          <button
            onClick={handleInitPeriode}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Plus className="h-4 w-4 inline mr-2" />
            Initialiser avec le catalogue
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Produit
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Prévu
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-purple-600 uppercase" title="Stock de départ (report + réserve boutique)">
                    Stock Départ
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-blue-600 uppercase">
                    Réservé
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-green-600 uppercase font-bold">
                    À Récolter
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Récolté
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Écart
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Statut
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {stocks.map((stock) => (
                  <tr 
                    key={stock.id} 
                    className={`hover:bg-gray-50 ${stock.statut === 'alerte_stock' ? 'bg-red-50' : ''}`}
                  >
                    {/* Produit */}
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        {stock.produit_image ? (
                          <img src={stock.produit_image} alt="" className="h-8 w-8 rounded object-cover" />
                        ) : (
                          <div className="h-8 w-8 bg-gray-100 rounded flex items-center justify-center">
                            <Leaf className="h-4 w-4 text-gray-400" />
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-gray-900 text-sm">{stock.produit_nom}</div>
                          <div className="text-xs text-gray-500">{stock.produit_unite}</div>
                        </div>
                      </div>
                    </td>

                    {/* Prévisionnel */}
                    <td className="px-3 py-3 text-center">
                      {editingId === stock.id ? (
                        <input
                          type="number"
                          step="0.1"
                          value={editValues.qte_previsionnelle}
                          onChange={(e) => setEditValues({...editValues, qte_previsionnelle: e.target.value})}
                          className="w-16 px-2 py-1 border rounded text-center text-sm"
                        />
                      ) : (
                        <span className="text-gray-700">{stock.qte_previsionnelle || 0}</span>
                      )}
                    </td>

                    {/* Stock Départ (ex-Talon) */}
                    <td className="px-3 py-3 text-center">
                      {editingId === stock.id ? (
                        <input
                          type="number"
                          step="0.1"
                          value={editValues.qte_talon_boutique}
                          onChange={(e) => setEditValues({...editValues, qte_talon_boutique: e.target.value})}
                          className="w-16 px-2 py-1 border rounded text-center text-sm border-purple-300"
                          title="Modifiable : si les vers ont bouffé les pommes 🐛"
                        />
                      ) : (
                        <span className="text-purple-600 font-medium">
                          {stock.qte_talon_boutique || 0}
                          {stock.qte_report_precedent > 0 && (
                            <span className="text-xs text-purple-400 ml-1" title="Dont report période précédente">
                              (↩{stock.qte_report_precedent})
                            </span>
                          )}
                        </span>
                      )}
                    </td>

                    {/* Réservé */}
                    <td className="px-3 py-3 text-center">
                      <span className="text-blue-600 font-medium">{stock.qte_reservee || 0}</span>
                      {stock.nb_clients_reserve > 0 && (
                        <span className="text-xs text-blue-400 ml-1">({stock.nb_clients_reserve} client{stock.nb_clients_reserve > 1 ? 's' : ''})</span>
                      )}
                    </td>

                    {/* À Récolter */}
                    <td className="px-3 py-3 text-center">
                      <span className="text-green-600 font-bold text-lg">{stock.qte_a_recolter || 0}</span>
                    </td>

                    {/* Récolté */}
                    <td className="px-3 py-3 text-center">
                      {editingId === stock.id ? (
                        <input
                          type="number"
                          step="0.1"
                          value={editValues.qte_recoltee}
                          onChange={(e) => setEditValues({...editValues, qte_recoltee: e.target.value})}
                          className="w-16 px-2 py-1 border rounded text-center text-sm"
                          placeholder="-"
                        />
                      ) : (
                        <span className={stock.qte_recoltee !== null ? 'font-medium' : 'text-gray-400'}>
                          {stock.qte_recoltee !== null ? stock.qte_recoltee : '-'}
                        </span>
                      )}
                    </td>

                    {/* Écart */}
                    <td className="px-3 py-3 text-center">
                      {stock.ecart_recolte !== null ? (
                        <span className={`flex items-center justify-center gap-1 font-medium ${
                          stock.ecart_recolte >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {stock.ecart_recolte >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                          {stock.ecart_recolte > 0 ? '+' : ''}{stock.ecart_recolte}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>

                    {/* Statut */}
                    <td className="px-3 py-3 text-center">
                      {getStatutBadge(stock.statut)}
                    </td>

                    {/* Actions */}
                    <td className="px-3 py-3 text-center">
                      {editingId === stock.id ? (
                        <div className="flex justify-center gap-1">
                          <button onClick={() => handleSaveEdit(stock.id)} className="p-1 text-green-600 hover:bg-green-100 rounded">
                            <Save className="h-5 w-5" />
                          </button>
                          <button onClick={handleCancelEdit} className="p-1 text-gray-600 hover:bg-gray-100 rounded">
                            <X className="h-5 w-5" />
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => handleEdit(stock)} className="p-1 text-blue-600 hover:bg-blue-100 rounded">
                          <Edit2 className="h-5 w-5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionRecolte;